/**
 * @fileoverview Bankruptcy case record fields
 * @module routes/dashboard/-ui.job-bankruptcy-case-record
 *
 * Used by:
 *  - Job Settings → Appointment Details, on a personal or business
 *    bankruptcy.
 *
 * Purpose:
 *  - Capture the facts that compute the commencement of bankruptcy under
 *    s 115 (MOR-1252): petition type, presentation date, date of bankruptcy,
 *    the act of bankruptcy relied on, whether a creditor's petition was
 *    pending on a debtor's petition, and the s 115(1A) extension flag.
 *  - Capture the insolvency evidence for an individual bankrupt (MOR-1255):
 *    whether a business was carried on, the earliest unpaid judgment and the
 *    statement-of-affairs inability date.
 *  - Preview the derived commencement through the real calculator so the
 *    section can never disagree with the backend, and report chronology
 *    blocks to the parent so Save is disabled (MOR-661 QA-05).
 *
 * Presentational: owns no state and no mutation, the same way
 * `RelationBackFieldsSection` does.
 */

import { useEffect } from "react";
import type {
  ActOfBankruptcyParagraph,
  BankruptcyPetitionType,
} from "@cvx/jobs/schema";
import {
  ACT_DATE_LABELS,
  deriveBankruptcyCommencement,
  DEEMED_DATE_PARAGRAPHS,
} from "@cvx/keyDates/bankruptcyCommencement";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Checkbox } from "@/ui/primitives/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { format, isValid, parseISO } from "date-fns";
import { Gavel } from "lucide-react";

export interface BankruptcyCaseRecordState {
  petitionType: BankruptcyPetitionType | "";
  petitionPresentedDate: string;
  dateOfBankruptcy: string;
  actOfBankruptcyDate: string;
  actOfBankruptcyParagraph: ActOfBankruptcyParagraph | "";
  actOfBankruptcyDescription: string;
  /** "" = unanswered. */
  creditorsPetitionPending: "" | "yes" | "no";
  courtSpecifiedCommencementDate: string;
  /** MOR-1255: "" = unanswered. */
  carriedOnBusiness: "" | "yes" | "no";
  earliestUnpaidJudgmentDate: string;
  statementOfAffairsInsolvencyDate: string;
  /** s 115(1A): the s 41(7) extension was found frivolous or vexatious. */
  extensionDisregarded: boolean;
}

export interface BankruptcyCaseRecordProps {
  values: BankruptcyCaseRecordState;
  onChange: (next: BankruptcyCaseRecordState) => void;
  /** Job dates the date-of-bankruptcy fallback reads. */
  context: { appointmentDate: string; courtOrderDate: string };
  /** Manual commencement override; omit `onOverrideChange` to hide it. */
  override?: string;
  onOverrideChange?: (next: string) => void;
  /** Reports false while the preview shows a chronology block. */
  onValidityChange?: (valid: boolean) => void;
  /** The job's appointment type; business bankruptcy pre-selects "carried on a business". */
  appointmentType: string;
  disabled: boolean;
}

const PETITION_TYPE_OPTIONS: Array<{
  value: BankruptcyPetitionType;
  label: string;
}> = [
  { value: "creditors_petition", label: "Creditor's petition" },
  { value: "debtors_petition", label: "Debtor's petition" },
  {
    value: "sequestration_order_other",
    label: "Sequestration order (Div 6 Part IV / Part X)",
  },
];

const PARAGRAPH_OPTIONS: Array<{
  value: ActOfBankruptcyParagraph;
  label: string;
}> = [
  { value: "g", label: "(g) Failure to comply with a bankruptcy notice" },
  { value: "daa", label: "(daa) Presented a debtor's petition" },
  { value: "da", label: "(da) Presented a s 54A declaration" },
  { value: "d", label: "(d) Execution levied or returned unsatisfied" },
  { value: "a", label: "(a) Assignment for the benefit of creditors" },
  { value: "b", label: "(b) Transfer void against the trustee" },
  { value: "c", label: "(c) Departing or absenting to defeat creditors" },
  { value: "e", label: "(e) Admitted insolvency at a creditors' meeting" },
  { value: "f", label: "(f) Failed to act after admitting insolvency" },
  { value: "h", label: "(h) Notice of suspension of payment" },
  { value: "ha", label: "(ha) Gave a debt agreement proposal" },
  { value: "hb", label: "(hb) Debt agreement accepted" },
  { value: "hc", label: "(hc) Debt agreement breached" },
  { value: "hd", label: "(hd) Debt agreement terminated" },
  { value: "i", label: "(i) Signed a s 188 authority" },
  { value: "j", label: "(j) Meeting called under a s 188 authority" },
  { value: "k", label: "(k) Failed to attend that meeting" },
  { value: "l", label: "(l) Failed to execute a PIA or present a petition" },
  {
    value: "m",
    label: "(m) Personal insolvency agreement set aside or terminated",
  },
  { value: "n", label: "(n) Composition or scheme set aside or terminated" },
  {
    value: "o",
    label: "(o) Insolvent after a family law financial agreement",
  },
];

/**
 * Bankruptcy case record section.
 *
 * @component
 * @example
 * <BankruptcyCaseRecordSection
 *   values={record}
 *   onChange={setRecord}
 *   context={{ appointmentDate, courtOrderDate }}
 *   override={override}
 *   onOverrideChange={setOverride}
 *   onValidityChange={setValid}
 *   appointmentType="personal_bankruptcy"
 *   disabled={false}
 * />
 */
export function BankruptcyCaseRecordSection({
  values,
  onChange,
  context,
  override = "",
  onOverrideChange,
  onValidityChange,
  appointmentType,
  disabled,
}: BankruptcyCaseRecordProps) {
  const isDebtors = values.petitionType === "debtors_petition";
  const isCreditors = values.petitionType === "creditors_petition";
  const isOrder = values.petitionType === "sequestration_order_other";
  const isBusinessBankruptcy = appointmentType === "business_bankruptcy";
  const pendingAnswered = values.creditorsPetitionPending !== "";
  const actRecorded =
    values.actOfBankruptcyParagraph !== "" ||
    values.actOfBankruptcyDescription.trim() !== "";

  // Preview through the real calculator so the section can never disagree
  // with the backend about which s 115 pathway applies.
  const preview = deriveBankruptcyCommencement({
    petitionType: values.petitionType || undefined,
    petitionPresentedDate: values.petitionPresentedDate || undefined,
    dateOfBankruptcy: values.dateOfBankruptcy || undefined,
    actOfBankruptcyDate: values.actOfBankruptcyDate || undefined,
    actOfBankruptcyParagraph: values.actOfBankruptcyParagraph || undefined,
    actOfBankruptcyRecorded: actRecorded,
    creditorsPetitionPending: pendingAnswered
      ? values.creditorsPetitionPending === "yes"
      : undefined,
    courtSpecifiedCommencementDate:
      values.courtSpecifiedCommencementDate || undefined,
    bankruptcyNoticeExtensionDisregarded: values.extensionDisregarded,
    courtOrderDate: context.courtOrderDate || undefined,
    appointmentDate: context.appointmentDate || undefined,
  });
  const chronologyBlocked = preview.blockedReason === "chronology";

  useEffect(() => {
    onValidityChange?.(!chronologyBlocked);
  }, [chronologyBlocked, onValidityChange]);

  const set = <K extends keyof BankruptcyCaseRecordState>(
    key: K,
    next: BankruptcyCaseRecordState[K],
  ) => onChange({ ...values, [key]: next });

  const deemed = values.actOfBankruptcyParagraph
    ? DEEMED_DATE_PARAGRAPHS.get(values.actOfBankruptcyParagraph)
    : undefined;
  const actDateLabel =
    (values.actOfBankruptcyParagraph
      ? ACT_DATE_LABELS[values.actOfBankruptcyParagraph]
      : undefined) ?? "Date of the act of bankruptcy";

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border/50 bg-secondary/20 p-4">
      <div className="flex items-start gap-2">
        <Gavel className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div>
          <h4 className="text-sm font-semibold">Bankruptcy Case Record</h4>
          <p className="text-xs text-muted-foreground">
            Sets the commencement of bankruptcy (s&nbsp;115), which anchors the
            s&nbsp;120 undervalued-transfer window. The s&nbsp;122 preference
            window runs from presentation of the petition unless a
            creditor&apos;s petition was pending.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label>Petition type</Label>
          <Select
            value={values.petitionType}
            onValueChange={(next) =>
              set("petitionType", next as BankruptcyPetitionType)
            }
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select petition type" />
            </SelectTrigger>
            <SelectContent>
              {PETITION_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>
            {isOrder
              ? "Date the application for the order was made"
              : "Date the petition was presented"}
          </Label>
          <DatePicker
            value={values.petitionPresentedDate}
            onChange={(next) => set("petitionPresentedDate", next ?? "")}
            placeholder="Select date"
            disabled={disabled}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Date of bankruptcy</Label>
          <DatePicker
            value={values.dateOfBankruptcy}
            onChange={(next) => set("dateOfBankruptcy", next ?? "")}
            placeholder="Defaults to the court order or appointment date"
            disabled={disabled}
          />
          <p className="text-[11px] leading-snug text-muted-foreground">
            The sequestration order, or acceptance of the debtor&apos;s
            petition. Closes every review window.
          </p>
        </div>

        {isDebtors && (
          <div className="flex flex-col gap-1.5">
            <Label>
              Was a creditor&apos;s petition pending when the debtor&apos;s
              petition was presented?
            </Label>
            <Select
              value={values.creditorsPetitionPending}
              onValueChange={(next) =>
                set("creditorsPetitionPending", next === "yes" ? "yes" : "no")
              }
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an answer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <Label>Act of bankruptcy relied on</Label>
          <Select
            value={values.actOfBankruptcyParagraph}
            onValueChange={(next) =>
              set("actOfBankruptcyParagraph", next as ActOfBankruptcyParagraph)
            }
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select s 40(1) paragraph" />
            </SelectTrigger>
            <SelectContent>
              {PARAGRAPH_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isDebtors && (
            <p className="text-[11px] leading-snug text-muted-foreground">
              Includes a breached debt agreement or a terminated personal
              insolvency agreement: those are acts of bankruptcy and can move
              commencement earlier.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>{actDateLabel}</Label>
          <DatePicker
            value={values.actOfBankruptcyDate}
            onChange={(next) => set("actOfBankruptcyDate", next ?? "")}
            placeholder="Select date"
            disabled={disabled}
          />
          {deemed && (
            <p className="text-[11px] leading-snug text-muted-foreground">
              Enter the underlying event date. The calculator applies the deemed
              commission date from {deemed}.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label htmlFor="act-of-bankruptcy-description">
            Description of the act
          </Label>
          <Input
            id="act-of-bankruptcy-description"
            value={values.actOfBankruptcyDescription}
            onChange={(e) => set("actOfBankruptcyDescription", e.target.value)}
            placeholder="e.g. Failed to comply with bankruptcy notice served 3 February 2026"
            disabled={disabled}
          />
        </div>

        {isCreditors && (
          <div className="flex items-start gap-2 sm:col-span-2">
            <Checkbox
              id="bankruptcy-notice-extension-disregarded"
              checked={values.extensionDisregarded}
              onCheckedChange={(checked) =>
                set("extensionDisregarded", checked === true)
              }
              disabled={disabled}
            />
            <Label
              htmlFor="bankruptcy-notice-extension-disregarded"
              className="text-xs font-normal leading-snug"
            >
              The s&nbsp;41(7) extension of time to comply with the bankruptcy
              notice was found frivolous, vexatious or without substantial merit
              (s&nbsp;115(1A)). Enter the act date that would have applied
              without the extension.
            </Label>
          </div>
        )}

        {isDebtors && (
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>Court-specified commencement (if any)</Label>
            <DatePicker
              value={values.courtSpecifiedCommencementDate}
              onChange={(next) =>
                set("courtSpecifiedCommencementDate", next ?? "")
              }
              placeholder="Only where the Court directed acceptance and fixed a time"
              disabled={disabled}
            />
          </div>
        )}
      </div>

      {/* Computed result: the whole point of the section. */}
      <div
        className={`flex flex-col gap-1 rounded-md border p-3 ${
          chronologyBlocked
            ? "border-destructive/50 bg-destructive/5"
            : "border-border/50 bg-background/60"
        }`}
      >
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-xs text-muted-foreground">
            Commencement of bankruptcy
          </span>
          <span className="text-sm font-semibold tabular-nums">
            {override
              ? formatDisplayDate(override)
              : preview.commencement
                ? formatDisplayDate(preview.commencement)
                : "Not yet available"}
          </span>
          {preview.section && !override && (
            <span className="text-[11px] text-muted-foreground">
              {preview.section}
            </span>
          )}
          {override && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
              Manual override
            </span>
          )}
          {preview.legacy && !override && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
              Petition type not recorded
            </span>
          )}
        </div>
        <p
          className={`text-[11px] leading-snug ${
            chronologyBlocked ? "text-destructive" : "text-muted-foreground"
          }`}
        >
          {formatDatesInCopy(preview.note)}
          {chronologyBlocked ? " Fix the dates before saving." : ""}
        </p>
        {preview.dateOfBankruptcy && (
          <p className="text-[11px] leading-snug text-muted-foreground">
            Date of bankruptcy {formatDisplayDate(preview.dateOfBankruptcy)}
            {preview.dateOfBankruptcyBasis === "court_order"
              ? " (from the court order date)"
              : preview.dateOfBankruptcyBasis === "appointment"
                ? " (from the appointment date)"
                : ""}
            .
          </p>
        )}
      </div>

      {onOverrideChange && (
        <div className="flex flex-col gap-1.5">
          <Label>Commencement Override</Label>
          <DatePicker
            value={override}
            onChange={(next) => onOverrideChange(next ?? "")}
            placeholder="Leave blank to use the computed date"
            disabled={disabled}
          />
          <p className="text-[11px] leading-snug text-muted-foreground">
            Overrides the computed commencement everywhere it is used. Clear it
            to return to the computed date.
          </p>
        </div>
      )}

      {/* MOR-1255: insolvency evidence for an individual bankrupt. */}
      <div className="flex flex-col gap-3 border-t border-border/50 pt-3">
        <div>
          <h5 className="text-xs font-semibold">Insolvency evidence</h5>
          <p className="text-[11px] leading-snug text-muted-foreground">
            The s&nbsp;122 preference head needs the trustee to prove insolvency
            at the time of each payment. The earliest of these dates becomes the
            estimated insolvency date.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <Label>Did the bankrupt carry on a business?</Label>
            <Select
              value={values.carriedOnBusiness}
              onValueChange={(next) =>
                set("carriedOnBusiness", next === "yes" ? "yes" : "no")
              }
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isBusinessBankruptcy
                      ? "Yes (business bankruptcy)"
                      : "Select an answer"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] leading-snug text-muted-foreground">
              Controls whether the Books &amp; Records presumptions
              (s&nbsp;120(3A), s&nbsp;121(4A)) can apply.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Earliest unpaid judgment or order</Label>
            <DatePicker
              value={values.earliestUnpaidJudgmentDate}
              onChange={(next) => set("earliestUnpaidJudgmentDate", next ?? "")}
              placeholder="Select date"
              disabled={disabled}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Unable to pay debts from (statement of affairs)</Label>
            <DatePicker
              value={values.statementOfAffairsInsolvencyDate}
              onChange={(next) =>
                set("statementOfAffairsInsolvencyDate", next ?? "")
              }
              placeholder="Select date"
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ISO `yyyy-MM-dd` → `dd/MM/yyyy` for display. Matches the DatePicker, so the
 * computed date and the date the practitioner just picked look the same.
 */
function formatDisplayDate(iso: string): string {
  const parsed = parseISO(iso);
  return isValid(parsed) ? format(parsed, "dd/MM/yyyy") : iso;
}

/** Rewrites ISO `yyyy-MM-dd` tokens inside a note to `dd/MM/yyyy`. */
function formatDatesInCopy(copy: string): string {
  return copy.replace(/\b\d{4}-\d{2}-\d{2}\b/g, formatDisplayDate);
}
